import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { CalendarClock } from 'lucide-react';
import { Calendar, Input } from '@vnetwork/ui/primitives';
import styles from './DateTimePicker.module.css';

/** 'HH:mm:ss' (or 'HH:mm') → seconds-of-day parts, defaulting missing fields to 0. */
function parseTime(s: string): { h: number; m: number; sec: number } {
  const [h, m, sec] = s.split(':').map((n) => Number(n) || 0);
  return { h: h ?? 0, m: m ?? 0, sec: sec ?? 0 };
}

/**
 * DS-native datetime picker: a Select-style trigger opening an in-flow
 * @vnetwork/ui Calendar (date) plus a native time Input — no portal, so it
 * plays nicely inside Dialog scroll-lock. Mirrors the DateRangePicker pattern
 * (outside-click + Esc to close). Emits a single epoch-ms value combining the
 * picked date with the entered time.
 */
export function DateTimePicker({
  value,
  onChange,
  disabled,
  placeholder = 'Select date and time',
}: {
  value: number | null;
  onChange: (ms: number) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  // Close on outside click and Escape (same house pattern as DateRangePicker).
  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const today = new Date();
  const selected = value != null ? new Date(value) : null;
  // Seconds are preserved internally; the time Input shows HH:mm:ss (step=1).
  const timeValue = value != null ? dayjs(value).format('HH:mm:ss') : '00:00:00';

  // Keep the chosen time-of-day when the date changes; default to midnight on
  // the very first pick (no value yet).
  const pickDate = (d: Date) => {
    const base = value != null ? dayjs(value) : dayjs(d).startOf('day');
    const next = dayjs(d).hour(base.hour()).minute(base.minute()).second(base.second()).millisecond(0);
    onChange(next.valueOf());
  };

  const pickTime = (raw: string) => {
    if (!raw) return;
    const { h, m, sec } = parseTime(raw);
    // Anchor to the already-selected date, or today if none picked yet.
    const base = value != null ? dayjs(value) : dayjs().startOf('day');
    onChange(base.hour(h).minute(m).second(sec).millisecond(0).valueOf());
  };

  const label = value != null ? dayjs(value).format('MMM D, YYYY · HH:mm') : placeholder;

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        className={styles.trigger}
        data-placeholder={value != null ? undefined : ''}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((o) => !o)}
      >
        <span className={styles.triggerLabel}>
          <CalendarClock size={15} className={styles.calendarIcon} />
          {label}
        </span>
      </button>

      {open && (
        <div className={styles.panel}>
          <Calendar
            value={selected}
            onChange={pickDate}
            today={today}
            defaultMonth={selected ?? today}
          />
          <div className={styles.timeRow}>
            <span className={styles.timeLabel}>Time</span>
            <Input
              type="time"
              step={1}
              value={timeValue}
              onChange={(e) => pickTime(e.target.value)}
              style={{ minWidth: 0 }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
